import React, {useEffect, useState, useRef, useCallback} from 'react'

interface Section {
  id: string
  title: string
  subsections?: {id: string; title: string}[]
}

const sections: Section[] = [
  {
    id: 'premiers-pas',
    title: '1. Premiers pas',
    subsections: [
      {id: 'connexion', title: 'Connexion'},
      {id: 'reinitialisation-mdp', title: 'Réinitialisation du mot de passe'},
      {id: 'navigation', title: 'Navigation dans l\'interface'},
    ],
  },
  {
    id: 'tableau-de-bord',
    title: '2. Tableau de bord',
    subsections: [
      {id: 'dashboard-presentation', title: 'Présentation'},
      {id: 'dashboard-kpi', title: 'Indicateurs clés (KPI)'},
      {id: 'dashboard-graphiques', title: 'Graphiques'},
    ],
  },
  {
    id: 'gestion-clients',
    title: '3. Gestion des clients',
    subsections: [
      {id: 'clients-creation', title: 'Création d\'un client'},
      {id: 'clients-edition', title: 'Édition et recherche'},
      {id: 'clients-contacts', title: 'Contacts et adresses'},
      {id: 'clients-documents', title: 'Documents'},
    ],
  },
  {
    id: 'gestion-vehicules',
    title: '4. Gestion des véhicules',
    subsections: [
      {id: 'vehicules-recherche', title: 'Recherche par immatriculation / CNIT'},
      {id: 'vehicules-fiche', title: 'Fiche véhicule'},
      {id: 'vehicules-stock', title: 'Stock de véhicules'},
    ],
  },
  {
    id: 'catalogues',
    title: '5. Catalogues',
    subsections: [
      {id: 'catalogues-consultation', title: 'Consultation'},
      {id: 'catalogues-recherche', title: 'Recherche avancée de pièces'},
      {id: 'catalogues-export', title: 'Export'},
    ],
  },
  {
    id: 'demandes',
    title: '6. Demandes',
    subsections: [
      {id: 'demandes-cycle', title: 'Cycle de vie d\'une demande'},
      {id: 'demandes-creation', title: 'Création d\'une demande'},
      {id: 'demandes-traitement', title: 'Traitement et proposition de pièces'},
      {id: 'demandes-cloture', title: 'Clôture'},
    ],
  },
  {
    id: 'depose-parc',
    title: '7. Dépose parc (Démontage)',
    subsections: [
      {id: 'demontage-vehicules', title: 'Démontage de véhicules'},
      {id: 'demontage-demandes', title: 'Demandes de démontage'},
      {id: 'demontage-transferts', title: 'Transferts'},
    ],
  },
  {
    id: 'pieces-pot',
    title: '8. Pièces POT',
    subsections: [
      {id: 'pot-gestion', title: 'Gestion des pièces obligatoires'},
      {id: 'pot-fournisseurs', title: 'Fournisseurs'},
    ],
  },
  {
    id: 'mes-actions',
    title: '9. Mes actions',
    subsections: [
      {id: 'actions-suivi', title: 'Suivi des actions'},
      {id: 'actions-types', title: 'Types d\'actions'},
    ],
  },
  {
    id: 'modification-prix',
    title: '10. Modification de prix',
    subsections: [
      {id: 'prix-import', title: 'Import'},
      {id: 'prix-modification', title: 'Modification'},
    ],
  },
  {
    id: 'parametres',
    title: '11. Paramètres',
    subsections: [
      {id: 'param-centres', title: 'Centres'},
      {id: 'param-genres', title: 'Genres'},
      {id: 'param-roles', title: 'Rôles'},
      {id: 'param-courriel', title: 'Configuration courriel'},
      {id: 'param-remises', title: 'Remises'},
      {id: 'param-motifs-refus', title: 'Motifs de refus'},
      {id: 'param-types-piece', title: 'Types de pièce'},
      {id: 'param-fournisseurs-pot', title: 'Fournisseurs POT'},
      {id: 'param-reseau-client', title: 'Réseau client'},
      {id: 'param-delais', title: 'Délais de traitement'},
    ],
  },
  {
    id: 'gestion-utilisateurs',
    title: '12. Gestion des utilisateurs',
    subsections: [
      {id: 'utilisateurs-creation', title: 'Création d\'un utilisateur'},
      {id: 'utilisateurs-roles', title: 'Rôles et permissions'},
    ],
  },
]

const DocumentationPage: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>('premiers-pas')
  const [showBackToTop, setShowBackToTop] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)

  const handleScroll = useCallback(() => {
    const scrollY = window.scrollY
    setShowBackToTop(scrollY > 300)

    const allIds = sections.flatMap((s) => [s.id, ...(s.subsections?.map((sub) => sub.id) || [])])

    // Si on est en bas de page, activer la dernière section visible
    const atBottom = window.innerHeight + scrollY >= document.documentElement.scrollHeight - 50
    if (atBottom) {
      // Remonter depuis la fin pour trouver le dernier élément présent dans le DOM
      for (let i = allIds.length - 1; i >= 0; i--) {
        if (document.getElementById(allIds[i])) {
          setActiveSection(allIds[i])
          return
        }
      }
    }

    let current = allIds[0]
    for (const id of allIds) {
      const el = document.getElementById(id)
      if (el) {
        const rect = el.getBoundingClientRect()
        if (rect.top <= 120) {
          current = id
        }
      }
    }
    setActiveSection(current)
  }, [])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, {passive: true})
    return () => window.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id)
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 100
      window.scrollTo({top, behavior: 'smooth'})
    }
  }

  const scrollToTop = () => {
    window.scrollTo({top: 0, behavior: 'smooth'})
  }

  const isActive = (id: string) => activeSection === id
  const isParentActive = (section: Section) =>
    activeSection === section.id ||
    (section.subsections?.some((sub) => sub.id === activeSection) ?? false)

  return (
    <div className='row g-5 g-xl-8'>
      {/* Sommaire latéral */}
      <div className='col-lg-3'>
        <div className='card card-flush' style={{position: 'sticky', top: '80px'}}>
          <div className='card-header pt-5'>
            <h3 className='card-title fw-bold text-dark'>Sommaire</h3>
          </div>
          <div className='card-body pt-2' style={{maxHeight: 'calc(100vh - 200px)', overflowY: 'auto'}}>
            <ul className='nav flex-column'>
              {sections.map((section) => (
                <li key={section.id} className='nav-item mb-1'>
                  <a
                    href={`#${section.id}`}
                    className={`nav-link py-2 px-3 rounded ${
                      isParentActive(section) ? 'bg-light-primary text-primary fw-bold' : 'text-gray-700'
                    }`}
                    onClick={(e) => {
                      e.preventDefault()
                      scrollToSection(section.id)
                    }}
                  >
                    {section.title}
                  </a>
                  {section.subsections && isParentActive(section) && (
                    <ul className='nav flex-column ps-4'>
                      {section.subsections.map((sub) => (
                        <li key={sub.id} className='nav-item'>
                          <a
                            href={`#${sub.id}`}
                            className={`nav-link py-1 px-3 rounded fs-7 ${
                              isActive(sub.id) ? 'text-primary fw-semibold' : 'text-gray-600'
                            }`}
                            onClick={(e) => {
                              e.preventDefault()
                              scrollToSection(sub.id)
                            }}
                          >
                            {sub.title}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Contenu */}
      <div className='col-lg-9' ref={contentRef}>
        {/* Introduction */}
        <div className='card card-flush mb-6'>
          <div className='card-body'>
            <h1 className='fw-bolder text-dark mb-4'>Documentation utilisateur — Koena</h1>
            <p className='text-gray-700 fs-5'>
              Bienvenue dans la documentation de l'application Koena. Ce guide couvre l'ensemble des
              fonctionnalités du portail de gestion de pièces automobiles recyclées. Utilisez le
              sommaire à gauche pour naviguer rapidement vers la section souhaitée.
            </p>
          </div>
        </div>

        {/* 1. Premiers pas */}
        <div className='card card-flush mb-6' id='premiers-pas'>
          <div className='card-header pt-5'>
            <h2 className='card-title fw-bolder text-dark'>1. Premiers pas</h2>
          </div>
          <div className='card-body'>
            <h4 id='connexion' className='fw-bold mb-3'>Connexion</h4>
            <p className='text-gray-700 mb-2'>
              Pour accéder à Koena, rendez-vous sur la page de connexion et saisissez votre adresse
              e-mail ainsi que votre mot de passe. Ces identifiants vous sont fournis par votre
              administrateur.
            </p>
            <ul className='text-gray-700 mb-6'>
              <li>Saisissez votre adresse e-mail dans le champ « Email ».</li>
              <li>Saisissez votre mot de passe dans le champ « Mot de passe ».</li>
              <li>Cliquez sur le bouton « Connexion ».</li>
            </ul>

            <h4 id='reinitialisation-mdp' className='fw-bold mb-3'>Réinitialisation du mot de passe</h4>
            <p className='text-gray-700 mb-2'>
              Si vous avez oublié votre mot de passe, cliquez sur le lien « Mot de passe oublié ? »
              sur la page de connexion.
            </p>
            <ul className='text-gray-700 mb-6'>
              <li>Cliquez sur « Mot de passe oublié ? ».</li>
              <li>Saisissez votre adresse e-mail et validez.</li>
              <li>Consultez votre boîte de réception et suivez le lien de réinitialisation.</li>
              <li>Définissez un nouveau mot de passe et confirmez-le.</li>
            </ul>

            <h4 id='navigation' className='fw-bold mb-3'>Navigation dans l'interface</h4>
            <p className='text-gray-700 mb-2'>
              L'interface de Koena se compose de plusieurs zones principales :
            </p>
            <ul className='text-gray-700'>
              <li><strong>Barre de navigation latérale (sidebar)</strong> : accès aux différentes sections de l'application (clients, véhicules, demandes, catalogues, etc.).</li>
              <li><strong>Barre supérieure (header)</strong> : accès rapide à la documentation, notifications et menu utilisateur.</li>
              <li><strong>Zone de contenu principale</strong> : affichage des pages, formulaires et tableaux de données.</li>
              <li><strong>Fil d'Ariane (breadcrumb)</strong> : situé en haut de la zone de contenu, il indique votre position dans l'arborescence de l'application.</li>
            </ul>
          </div>
        </div>

        {/* 2. Tableau de bord */}
        <div className='card card-flush mb-6' id='tableau-de-bord'>
          <div className='card-header pt-5'>
            <h2 className='card-title fw-bolder text-dark'>2. Tableau de bord</h2>
          </div>
          <div className='card-body'>
            <h4 id='dashboard-presentation' className='fw-bold mb-3'>Présentation</h4>
            <p className='text-gray-700 mb-6'>
              Le tableau de bord est la page d'accueil après connexion. Il offre une vue synthétique
              de l'activité de votre centre : demandes en cours, pièces en stock, indicateurs de
              performance et graphiques. C'est le point de départ idéal pour suivre l'état global de
              votre activité au quotidien.
            </p>

            <h4 id='dashboard-kpi' className='fw-bold mb-3'>Indicateurs clés (KPI)</h4>
            <p className='text-gray-700 mb-2'>
              Les indicateurs clés de performance sont affichés sous forme de cartes en haut du
              tableau de bord. Ils vous permettent de visualiser rapidement :
            </p>
            <ul className='text-gray-700 mb-6'>
              <li>Le nombre de demandes en attente de traitement.</li>
              <li>Le nombre de pièces disponibles en stock.</li>
              <li>Le chiffre d'affaires sur la période sélectionnée.</li>
              <li>Les taux de conversion et de satisfaction.</li>
            </ul>

            <h4 id='dashboard-graphiques' className='fw-bold mb-3'>Graphiques</h4>
            <p className='text-gray-700'>
              Des graphiques interactifs vous permettent de suivre l'évolution de votre activité dans
              le temps : courbes de ventes, répartition des demandes par statut, comparaisons entre
              périodes. Survolez les graphiques pour afficher les détails et utilisez les filtres pour
              ajuster la période d'analyse.
            </p>
          </div>
        </div>

        {/* 3. Gestion des clients */}
        <div className='card card-flush mb-6' id='gestion-clients'>
          <div className='card-header pt-5'>
            <h2 className='card-title fw-bolder text-dark'>3. Gestion des clients</h2>
          </div>
          <div className='card-body'>
            <h4 id='clients-creation' className='fw-bold mb-3'>Création d'un client</h4>
            <p className='text-gray-700 mb-2'>
              Pour créer un nouveau client, accédez à la section « Vente » puis « Clients » dans le
              menu latéral.
            </p>
            <ul className='text-gray-700 mb-6'>
              <li>Cliquez sur le bouton « Ajouter un client ».</li>
              <li>Renseignez les informations obligatoires : nom, prénom (ou raison sociale), e-mail, téléphone.</li>
              <li>Sélectionnez le type de client (particulier, professionnel, réseau).</li>
              <li>Validez en cliquant sur « Enregistrer ».</li>
            </ul>

            <h4 id='clients-edition' className='fw-bold mb-3'>Édition et recherche</h4>
            <p className='text-gray-700 mb-2'>
              La liste des clients est accessible depuis la section « Vente &gt; Clients ». Vous pouvez :
            </p>
            <ul className='text-gray-700 mb-6'>
              <li>Rechercher un client par nom, e-mail ou téléphone via la barre de recherche.</li>
              <li>Filtrer la liste par type de client ou par statut.</li>
              <li>Cliquer sur un client pour accéder à sa fiche détaillée et la modifier.</li>
            </ul>

            <h4 id='clients-contacts' className='fw-bold mb-3'>Contacts et adresses</h4>
            <p className='text-gray-700 mb-6'>
              Sur la fiche d'un client, l'onglet « Contacts » permet de gérer les personnes de
              contact associées (nom, fonction, téléphone, e-mail). L'onglet « Adresses » permet
              d'enregistrer les adresses de livraison et de facturation.
            </p>

            <h4 id='clients-documents' className='fw-bold mb-3'>Documents</h4>
            <p className='text-gray-700'>
              L'onglet « Documents » de la fiche client permet de consulter et télécharger les
              documents associés au client : bons de commande, factures, devis, etc.
            </p>
          </div>
        </div>

        {/* 4. Gestion des véhicules */}
        <div className='card card-flush mb-6' id='gestion-vehicules'>
          <div className='card-header pt-5'>
            <h2 className='card-title fw-bolder text-dark'>4. Gestion des véhicules</h2>
          </div>
          <div className='card-body'>
            <h4 id='vehicules-recherche' className='fw-bold mb-3'>Recherche par immatriculation / CNIT</h4>
            <p className='text-gray-700 mb-2'>
              La recherche de véhicules est accessible depuis le menu « Véhicules ». Deux modes de
              recherche sont disponibles :
            </p>
            <ul className='text-gray-700 mb-6'>
              <li><strong>Par immatriculation</strong> : saisissez le numéro d'immatriculation complet pour retrouver un véhicule spécifique.</li>
              <li><strong>Par CNIT</strong> : saisissez le Code National d'Identification du Type pour rechercher un modèle de véhicule.</li>
            </ul>

            <h4 id='vehicules-fiche' className='fw-bold mb-3'>Fiche véhicule</h4>
            <p className='text-gray-700 mb-6'>
              La fiche véhicule présente l'ensemble des informations techniques : marque, modèle,
              version, motorisation, année de mise en circulation, kilométrage. Vous y retrouvez
              également la liste des pièces disponibles issues de ce véhicule et l'historique des
              opérations réalisées.
            </p>

            <h4 id='vehicules-stock' className='fw-bold mb-3'>Stock de véhicules</h4>
            <p className='text-gray-700'>
              La vue stock permet de consulter l'ensemble des véhicules présents sur le parc. Vous
              pouvez filtrer par statut (en attente de démontage, en cours, démonté, etc.) et
              consulter les pièces déjà extraites pour chaque véhicule.
            </p>
          </div>
        </div>

        {/* 5. Catalogues */}
        <div className='card card-flush mb-6' id='catalogues'>
          <div className='card-header pt-5'>
            <h2 className='card-title fw-bolder text-dark'>5. Catalogues</h2>
          </div>
          <div className='card-body'>
            <h4 id='catalogues-consultation' className='fw-bold mb-3'>Consultation</h4>
            <p className='text-gray-700 mb-6'>
              Le catalogue regroupe l'ensemble des pièces disponibles à la vente. Accessible depuis
              le menu « Catalogues », il permet de parcourir les pièces par catégorie, par véhicule
              d'origine ou par référence. Chaque fiche pièce affiche les informations essentielles :
              désignation, référence, prix, état, disponibilité et véhicule d'origine.
            </p>

            <h4 id='catalogues-recherche' className='fw-bold mb-3'>Recherche avancée de pièces</h4>
            <p className='text-gray-700 mb-2'>
              La recherche avancée permet de combiner plusieurs critères pour trouver des pièces
              spécifiques :
            </p>
            <ul className='text-gray-700 mb-6'>
              <li>Recherche par désignation ou référence de pièce.</li>
              <li>Filtrage par marque, modèle ou année du véhicule d'origine.</li>
              <li>Filtrage par état de la pièce (neuf, occasion, reconditionné).</li>
              <li>Filtrage par fourchette de prix.</li>
            </ul>

            <h4 id='catalogues-export' className='fw-bold mb-3'>Export</h4>
            <p className='text-gray-700'>
              Il est possible d'exporter le catalogue ou une sélection de pièces au format CSV ou
              Excel. Utilisez le bouton « Exporter » en haut de la liste pour lancer l'export avec
              les filtres actuellement appliqués.
            </p>
          </div>
        </div>

        {/* 6. Demandes */}
        <div className='card card-flush mb-6' id='demandes'>
          <div className='card-header pt-5'>
            <h2 className='card-title fw-bolder text-dark'>6. Demandes</h2>
          </div>
          <div className='card-body'>
            <h4 id='demandes-cycle' className='fw-bold mb-3'>Cycle de vie d'une demande</h4>
            <p className='text-gray-700 mb-2'>
              Une demande passe par plusieurs statuts au cours de son cycle de vie :
            </p>
            <ul className='text-gray-700 mb-6'>
              <li><strong>Nouvelle</strong> : la demande vient d'être créée.</li>
              <li><strong>En cours de traitement</strong> : un opérateur a pris en charge la demande et recherche les pièces.</li>
              <li><strong>Proposition envoyée</strong> : une ou plusieurs pièces ont été proposées au client.</li>
              <li><strong>Acceptée</strong> : le client a validé la proposition.</li>
              <li><strong>Refusée</strong> : le client a décliné la proposition.</li>
              <li><strong>Clôturée</strong> : la demande est finalisée (vente conclue ou demande abandonnée).</li>
            </ul>

            <h4 id='demandes-creation' className='fw-bold mb-3'>Création d'une demande</h4>
            <p className='text-gray-700 mb-2'>
              Pour créer une nouvelle demande :
            </p>
            <ul className='text-gray-700 mb-6'>
              <li>Accédez à « Demandes » dans le menu latéral.</li>
              <li>Cliquez sur « Nouvelle demande ».</li>
              <li>Sélectionnez le client concerné (ou créez-en un nouveau).</li>
              <li>Renseignez le véhicule du client et la ou les pièces recherchées.</li>
              <li>Validez la création de la demande.</li>
            </ul>

            <h4 id='demandes-traitement' className='fw-bold mb-3'>Traitement et proposition de pièces</h4>
            <p className='text-gray-700 mb-6'>
              Une fois la demande créée, un opérateur peut la prendre en charge. Depuis la fiche de
              la demande, il recherche les pièces correspondantes dans le catalogue, les ajoute à la
              proposition, ajuste les prix si nécessaire, puis envoie la proposition au client.
            </p>

            <h4 id='demandes-cloture' className='fw-bold mb-3'>Clôture</h4>
            <p className='text-gray-700'>
              Une demande peut être clôturée lorsqu'elle aboutit à une vente ou lorsque le client
              refuse la proposition. Un motif de clôture peut être renseigné pour le suivi
              statistique. Les demandes clôturées restent consultables dans l'historique.
            </p>
          </div>
        </div>

        {/* 7. Dépose parc (Démontage) */}
        <div className='card card-flush mb-6' id='depose-parc'>
          <div className='card-header pt-5'>
            <h2 className='card-title fw-bolder text-dark'>7. Dépose parc (Démontage)</h2>
          </div>
          <div className='card-body'>
            <h4 id='demontage-vehicules' className='fw-bold mb-3'>Démontage de véhicules</h4>
            <p className='text-gray-700 mb-6'>
              Le module de démontage permet de gérer le processus de dépose des pièces sur les
              véhicules du parc. Depuis la section « Démontage », vous accédez à la liste des
              véhicules en attente ou en cours de démontage. Pour chaque véhicule, vous pouvez
              enregistrer les pièces déposées, leur état et leur emplacement de stockage.
            </p>

            <h4 id='demontage-demandes' className='fw-bold mb-3'>Demandes de démontage</h4>
            <p className='text-gray-700 mb-6'>
              Les demandes de démontage permettent de signaler qu'une pièce spécifique doit être
              déposée d'un véhicule donné (par exemple, suite à une demande client). Elles sont
              visibles dans la section « Demandes de démontage » et peuvent être priorisées et
              assignées.
            </p>

            <h4 id='demontage-transferts' className='fw-bold mb-3'>Transferts</h4>
            <p className='text-gray-700'>
              Les transferts permettent de déplacer des pièces entre différents centres ou
              emplacements de stockage. Créez une demande de transfert en indiquant la pièce, le
              centre d'origine et le centre de destination. Le transfert suit un workflow de
              validation avant expédition.
            </p>
          </div>
        </div>

        {/* 8. Pièces POT */}
        <div className='card card-flush mb-6' id='pieces-pot'>
          <div className='card-header pt-5'>
            <h2 className='card-title fw-bolder text-dark'>8. Pièces POT</h2>
          </div>
          <div className='card-body'>
            <h4 id='pot-gestion' className='fw-bold mb-3'>Gestion des pièces obligatoires</h4>
            <p className='text-gray-700 mb-6'>
              Les pièces POT (Pièces issues des Opérations de Traitement) sont des pièces
              réglementées qui doivent être obligatoirement extraites lors du traitement d'un véhicule
              hors d'usage. Cette section permet de suivre l'état d'extraction de ces pièces pour
              chaque véhicule, de valider leur traitement et de générer les documents de traçabilité
              réglementaire.
            </p>

            <h4 id='pot-fournisseurs' className='fw-bold mb-3'>Fournisseurs</h4>
            <p className='text-gray-700'>
              La gestion des fournisseurs POT permet de référencer les prestataires habilités à
              traiter les pièces réglementées. Vous pouvez ajouter, modifier et consulter les
              fournisseurs depuis la section « Fournisseurs POT » dans les paramètres.
            </p>
          </div>
        </div>

        {/* 9. Mes actions */}
        <div className='card card-flush mb-6' id='mes-actions'>
          <div className='card-header pt-5'>
            <h2 className='card-title fw-bolder text-dark'>9. Mes actions</h2>
          </div>
          <div className='card-body'>
            <h4 id='actions-suivi' className='fw-bold mb-3'>Suivi des actions</h4>
            <p className='text-gray-700 mb-6'>
              La section « Mes actions » centralise l'ensemble des tâches et rappels qui vous sont
              assignés. Chaque action possède un type, une date d'échéance, un statut (à faire, en
              cours, terminée) et peut être rattachée à un client, une demande ou un véhicule. Cette
              vue vous permet de prioriser votre travail au quotidien.
            </p>

            <h4 id='actions-types' className='fw-bold mb-3'>Types d'actions</h4>
            <p className='text-gray-700'>
              Les types d'actions sont configurables depuis les paramètres. Ils permettent de
              catégoriser les actions (rappel client, relance devis, vérification stock, etc.) et
              d'organiser le travail par thématique.
            </p>
          </div>
        </div>

        {/* 10. Modification de prix */}
        <div className='card card-flush mb-6' id='modification-prix'>
          <div className='card-header pt-5'>
            <h2 className='card-title fw-bolder text-dark'>10. Modification de prix</h2>
          </div>
          <div className='card-body'>
            <h4 id='prix-import' className='fw-bold mb-3'>Import</h4>
            <p className='text-gray-700 mb-6'>
              La fonctionnalité d'import de prix permet de mettre à jour en masse les tarifs des
              pièces. Préparez un fichier CSV ou Excel conforme au modèle fourni, puis importez-le
              depuis la section « Modification de prix ». Le système vérifie la cohérence des données
              avant d'appliquer les changements.
            </p>

            <h4 id='prix-modification' className='fw-bold mb-3'>Modification</h4>
            <p className='text-gray-700'>
              Vous pouvez également modifier les prix unitairement depuis la fiche d'une pièce, ou en
              sélectionnant plusieurs pièces dans le catalogue pour appliquer une modification
              groupée (pourcentage d'augmentation ou de réduction, prix fixe).
            </p>
          </div>
        </div>

        {/* 11. Paramètres */}
        <div className='card card-flush mb-6' id='parametres'>
          <div className='card-header pt-5'>
            <h2 className='card-title fw-bolder text-dark'>11. Paramètres</h2>
          </div>
          <div className='card-body'>
            <p className='text-gray-700 mb-4'>
              La section Paramètres regroupe l'ensemble des configurations de l'application. Selon
              vos droits d'accès, vous pouvez gérer :
            </p>

            <h4 id='param-centres' className='fw-bold mb-3'>Centres</h4>
            <p className='text-gray-700 mb-6'>
              Gestion des centres de recyclage : création, modification des informations (adresse,
              téléphone, horaires). Chaque centre dispose de son propre stock de véhicules et de
              pièces.
            </p>

            <h4 id='param-genres' className='fw-bold mb-3'>Genres</h4>
            <p className='text-gray-700 mb-6'>
              Les genres correspondent aux catégories de véhicules (VP, VUL, PL, etc.). Ce
              référentiel permet de classifier les véhicules entrant sur le parc.
            </p>

            <h4 id='param-roles' className='fw-bold mb-3'>Rôles</h4>
            <p className='text-gray-700 mb-6'>
              Les rôles définissent les niveaux d'accès des utilisateurs. Chaque rôle regroupe un
              ensemble de permissions qui déterminent les sections et actions accessibles dans
              l'application.
            </p>

            <h4 id='param-courriel' className='fw-bold mb-3'>Configuration courriel</h4>
            <p className='text-gray-700 mb-6'>
              Configuration des modèles d'e-mails envoyés par l'application : notifications de
              demande, confirmations, relances. Vous pouvez personnaliser le contenu, l'objet et les
              destinataires pour chaque type de courriel.
            </p>

            <h4 id='param-remises' className='fw-bold mb-3'>Remises</h4>
            <p className='text-gray-700 mb-6'>
              Gestion des barèmes de remises applicables aux clients. Vous pouvez définir des
              pourcentages de remise par type de client, par catégorie de pièce ou par volume.
            </p>

            <h4 id='param-motifs-refus' className='fw-bold mb-3'>Motifs de refus</h4>
            <p className='text-gray-700 mb-6'>
              Le référentiel des motifs de refus permet de catégoriser les raisons pour lesquelles un
              client refuse une proposition. Ces données sont utilisées à des fins statistiques pour
              améliorer le taux de conversion.
            </p>

            <h4 id='param-types-piece' className='fw-bold mb-3'>Types de pièce</h4>
            <p className='text-gray-700 mb-6'>
              Gestion du référentiel des types de pièces : moteur, carrosserie, optique, mécanique,
              etc. Ce référentiel est utilisé pour classifier les pièces dans le catalogue.
            </p>

            <h4 id='param-fournisseurs-pot' className='fw-bold mb-3'>Fournisseurs POT</h4>
            <p className='text-gray-700 mb-6'>
              Référentiel des fournisseurs habilités pour le traitement des pièces POT
              réglementées. Ajoutez ou modifiez les informations des prestataires.
            </p>

            <h4 id='param-reseau-client' className='fw-bold mb-3'>Réseau client</h4>
            <p className='text-gray-700 mb-6'>
              Configuration des réseaux clients permettant de regrouper des clients sous une même
              enseigne ou un même groupe, avec des conditions commerciales partagées.
            </p>

            <h4 id='param-delais' className='fw-bold mb-3'>Délais de traitement</h4>
            <p className='text-gray-700'>
              Paramétrage des délais de traitement attendus pour les différents types de demandes.
              Ces délais sont utilisés pour le suivi de la performance et l'alerte en cas de
              dépassement.
            </p>
          </div>
        </div>

        {/* 12. Gestion des utilisateurs */}
        <div className='card card-flush mb-6' id='gestion-utilisateurs'>
          <div className='card-header pt-5'>
            <h2 className='card-title fw-bolder text-dark'>12. Gestion des utilisateurs</h2>
          </div>
          <div className='card-body'>
            <h4 id='utilisateurs-creation' className='fw-bold mb-3'>Création d'un utilisateur</h4>
            <p className='text-gray-700 mb-2'>
              Pour ajouter un nouvel utilisateur, accédez à la section « Utilisateurs » dans le menu
              latéral.
            </p>
            <ul className='text-gray-700 mb-6'>
              <li>Cliquez sur « Ajouter un utilisateur ».</li>
              <li>Renseignez les informations : nom, prénom, e-mail, téléphone.</li>
              <li>Sélectionnez le rôle à attribuer (administrateur, opérateur, etc.).</li>
              <li>Assignez un ou plusieurs centres d'accès.</li>
              <li>Validez la création. L'utilisateur recevra un e-mail avec ses identifiants.</li>
            </ul>

            <h4 id='utilisateurs-roles' className='fw-bold mb-3'>Rôles et permissions</h4>
            <p className='text-gray-700 mb-2'>
              Chaque utilisateur se voit attribuer un rôle qui détermine ses droits d'accès. Les
              permissions sont organisées par module :
            </p>
            <ul className='text-gray-700'>
              <li><strong>Lecture</strong> : consultation des données sans possibilité de modification.</li>
              <li><strong>Écriture</strong> : création et modification des données.</li>
              <li><strong>Suppression</strong> : suppression de données existantes.</li>
              <li><strong>Administration</strong> : accès à la configuration et aux paramètres avancés.</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bouton retour en haut */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className='btn btn-icon btn-primary btn-sm shadow'
          style={{
            position: 'fixed',
            bottom: '30px',
            right: '30px',
            zIndex: 1000,
            borderRadius: '50%',
            width: '40px',
            height: '40px',
          }}
          title='Retour en haut'
        >
          <i className='bi bi-arrow-up fs-4'></i>
        </button>
      )}
    </div>
  )
}

export {DocumentationPage}
